import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import { useAuth } from '../../contexts/AuthContext';

const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const PUBLIC_PLAN_KEYS = ['single', 'pack3', 'pack5', 'sprint'];


const loadRazorpay = () => new Promise((resolve, reject) => {
    if (window.Razorpay) {
        resolve(window.Razorpay);
        return;
    }

    const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.Razorpay));
        existingScript.addEventListener('error', () => reject(new Error('Unable to load Razorpay checkout')));
        return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => window.Razorpay ? resolve(window.Razorpay) : reject(new Error('Razorpay checkout unavailable'));
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
    document.body.appendChild(script);
});

const readText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        if (value.Valid === false) return '';
        return value.String || value.string || '';
    }
    return String(value);
};

const readNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return Number(value) || 0;
    if (typeof value === 'object') {
        if (value.Valid === false) return 0;
        return Number(value.Int32 ?? value.Int64 ?? value.Int ?? value.float64 ?? 0) || 0;
    }
    return 0;
};

const packMetadata = [
    {
        key: 'single',
        aliases: ['single', 'starter', 'perinterview', '1interview', 'oneinterview'],
        interviews: 1,
        badge: null,
        bestFor: 'One focused AI mock interview',
        features: ['AI mock interview access', 'Performance report and coding evaluation', 'Resume-based personalization'],
    },
    {
        key: 'pack3',
        aliases: ['pack3', 'packof3', '3interviews', 'threeinterviews', 'popular'],
        interviews: 3,
        badge: 'Most Popular',
        bestFor: 'Build confidence across three attempts',
        features: ['AI mock interview access', 'Performance report and coding evaluation', 'Resume-based personalization'],
    },
    {
        key: 'pack5',
        aliases: ['pack5', 'interviewready', '5interviews', 'fiveinterviews'],
        interviews: 5,
        badge: null,
        bestFor: 'Complete multi-company interview preparation',
        features: ['AI mock interview access', 'Performance report and coding evaluation', 'Resume-based personalization'],
    },
    {
        key: 'sprint',
        aliases: ['sprint', 'sprintplan'],
        interviews: null,
        badge: null,
        bestFor: 'A focused interview preparation sprint',
        features: ['AI mock interview access', 'Performance report and coding evaluation', 'Resume-based personalization'],
    },
];

const getPackMeta = (pack) => {
    const searchable = normalize(`${pack.key} ${pack.name} ${pack.description}`);
    return packMetadata.find((meta) => meta.aliases.some((alias) => searchable.includes(alias))) || {
        key: 'custom',
        interviews: null,
        badge: null,
        bestFor: pack.description || 'Flexible interview practice',
        features: ['AI mock interview credits', 'Detailed performance report', 'Use coins across available interview types'],
    };
};

const mapPlan = (plan) => {
    const pricePaise = readNumber(plan.price_paise);
    const mapped = {
        id: plan.key || plan.id,
        key: plan.key,
        name: readText(plan.name) || 'Interview Pack',
        description: readText(plan.description),
        price: pricePaise / 100,
        pricePaise,
        coinsIncluded: readNumber(plan.coins_included),
        originalPrice: readNumber(plan.original_price_paise) / 100 || pricePaise / 100,
        discountPercent: readNumber(plan.original_price_paise) > pricePaise ? Math.round((1 - pricePaise / readNumber(plan.original_price_paise)) * 100) : 0,
        offerName: readText(plan.campaign_name) || 'Offer',
    };

    return {
        ...mapped,
        meta: getPackMeta(mapped),
    };
};

const matchesRequestedPlan = (plan, requestedPlanId) => {
    if (!requestedPlanId) return false;
    const requested = normalize(requestedPlanId);
    const searchable = normalize(`${plan.id} ${plan.key} ${plan.name} ${plan.description} ${plan.meta.key}`);
    return searchable.includes(requested) || requested.includes(normalize(plan.meta.key));
};

const PackCard = ({ pack, selected, onClick }) => {
    const isPopular = Boolean(pack.meta.badge || normalize(pack.name).includes('popular'));

    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={`relative flex min-h-[228px] flex-col rounded-2xl border p-4 text-left transition-all duration-200 ${
                selected
                    ? 'border-emerald-500 bg-emerald-50/60 shadow-[0_10px_30px_-18px_rgba(16,185,129,0.8)]'
                    : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-[0_10px_30px_-20px_rgba(15,23,42,0.35)]'
            }`}
        >
            {isPopular && (
                <div className="absolute -top-3 left-4 rounded-full bg-emerald-500 px-2.5 py-1 text-[9px] font-black  tracking-[0.14em] text-white shadow-md shadow-emerald-200">
                    {pack.meta.badge || 'Popular'}
                </div>
            )}

            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-[9px] font-black  tracking-[0.16em] text-emerald-600">
                        {pack.meta.interviews ? `${pack.meta.interviews} Interview${pack.meta.interviews > 1 ? 's' : ''}` : 'Coin Pack'}
                    </p>
                    <h3 className="mt-1.5 text-[17px] font-extrabold tracking-tight text-slate-900">{pack.name}</h3>
                </div>
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
                    <Icon name="Check" size={14} strokeWidth={3} />
                </div>
            </div>

            <p className="mt-2 min-h-[36px] text-xs font-medium leading-5 text-slate-500">
                {pack.description || pack.meta.bestFor}
            </p>

            <div className="mt-3 flex items-end justify-between gap-3 border-t border-slate-200/80 pt-3">
                <div>
                    {pack.discountPercent > 0 && <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400 line-through">₹{pack.originalPrice}</span>
                        <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-extrabold tracking-wide text-emerald-700">{pack.offerName}</span>
                    </div>}
                    <div className="mt-0.5">
                        <span className="text-[22px] font-black tracking-tight text-slate-900">₹{pack.price}</span>
                        <span className="ml-1 text-[10px] font-semibold text-slate-400">one-time</span>
                    </div>
                </div>
                {pack.coinsIncluded > 0 && (
                    <div className="text-right">
                        <p className="text-[9px] font-bold  tracking-[0.14em] text-slate-400">Credits</p>
                        <p className="text-sm font-extrabold text-emerald-600">{pack.coinsIncluded}</p>
                    </div>
                )}
            </div>

            <ul className="mt-3 space-y-1.5">
                {pack.meta.features.slice(0, 2).map((feature) => (
                    <li key={feature} className="flex items-start gap-1.5 text-[11px] font-medium leading-4 text-slate-600">
                        <Icon name="Check" size={12} className="mt-0.5 shrink-0 text-emerald-500" strokeWidth={3} />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
        </button>
    );
};

const Checkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { checkAuth } = useAuth();
    const requestedPlanId = location.state?.planId || (!['coin', 'structured', 'ai'].includes(location.state?.preselect) ? location.state?.preselect : null);

    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [selectedPackId, setSelectedPackId] = useState(null);

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [processing, setProcessing] = useState(null);
    const [success, setSuccess] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const res = await api.get('/api/plans', {
                    // Pricing can change from the admin dashboard, so every
                    // checkout visit must read the current catalog.
                    params: { _t: Date.now() },
                    headers: {
                        'Cache-Control': 'no-cache, no-store',
                        Pragma: 'no-cache',
                    },
                });
                // The backend already returns only the public fixed-plan catalog.
                // Do not apply a second allowlist here, otherwise an active plan
                // created from the admin dashboard (such as Sprint) can disappear.
                const mappedPlans = (res.data.plans || [])
                    .map(mapPlan)
                    .sort((a, b) => {
                        const aIndex = PUBLIC_PLAN_KEYS.indexOf(a.key);
                        const bIndex = PUBLIC_PLAN_KEYS.indexOf(b.key);
                        return (aIndex < 0 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex < 0 ? Number.MAX_SAFE_INTEGER : bIndex);
                    });
                const requestedPlan = mappedPlans.find((plan) => matchesRequestedPlan(plan, requestedPlanId));
                                const popularPlan = mappedPlans.find((plan) => plan.key === 'pack3');

                setPlans(mappedPlans);
                setSelectedPackId((requestedPlan || popularPlan || mappedPlans[0])?.id || null);
            } catch (error) {
                console.error(error);
                setErrors({ main: 'Unable to load pricing packs right now.' });
            } finally {
                setLoadingPlans(false);
            }
        };

        fetchPlans();
    }, [requestedPlanId]);

    const selectedPlan = plans.find((plan) => plan.id === selectedPackId);
    const basePrice = selectedPlan?.price || 0;
    const discountAmount = appliedCoupon?.discount_amount ? appliedCoupon.discount_amount / 100 : 0;
    const finalPrice = Math.max(0, basePrice - discountAmount);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim() || !selectedPlan) return;
        setValidatingCoupon(true);
        setCouponError(null);

        try {
            const response = await api.post('/api/coupons/validate', {
                code: couponCode.trim(),
                amount: selectedPlan.pricePaise,
            });

            if (response.data.valid) {
                setAppliedCoupon(response.data);
            } else {
                setCouponError('Invalid coupon code');
            }
        } catch (error) {
            setAppliedCoupon(null);
            setCouponError(error.response?.data?.error || 'Invalid or expired coupon');
        } finally {
            setValidatingCoupon(false);
        }
    };

    const handlePayment = async () => {
        if (!selectedPlan) return;
        setProcessing(selectedPlan.id);
        setErrors({});

        try {
            const orderResponse = await api.post('/api/create-order', {
                plan_id: selectedPlan.key,
                coupon_code: appliedCoupon?.code,
            });

            const Razorpay = await loadRazorpay();
            const order = orderResponse.data;
            const checkout = new Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency || 'INR',
                name: 'prepxai',
                description: selectedPlan.name,
                order_id: order.order_id,
                theme: { color: '#10b981' },
                handler: async (paymentResponse) => {
                    try {
                        await api.post('/api/verify-payment', paymentResponse);
                        await checkAuth();
                        setSuccessMsg(`${selectedPlan.name} added to your account!`);
                        setSuccess(true);
                    } catch (verificationError) {
                        setErrors({ main: verificationError.response?.data?.error || 'Payment verification failed. Please contact support if you were charged.' });
                    } finally {
                        setProcessing(null);
                    }
                },
                modal: {
                    ondismiss: () => setProcessing(null),
                },
            });
            checkout.open();
        } catch (err) {
            setErrors({ main: err.response?.data?.error || err.message || 'Unable to start payment' });
            setProcessing(null);
        } finally {
            // Razorpay owns the rest of the payment lifecycle after checkout.open().
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[linear-gradient(180deg,#ecfdf5_0%,#f7faf9_34%,#f7faf9_100%)] font-sans text-slate-900">
                <NavigationHeader />

                <main className="flex min-h-[calc(100vh-64px)] items-center justify-center px-5 py-10 sm:px-8">
                    <motion.div
                        initial={{ y: 12, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.25 }}
                        className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-35px_rgba(15,23,42,0.35)]"
                    >
                        <div className="border-b border-emerald-100 bg-emerald-50/70 px-6 py-4">
                            <div className="flex items-center gap-2 text-xs font-bold  tracking-[0.14em] text-emerald-700">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                                    <Icon name="Check" size={12} strokeWidth={3} />
                                </span>
                                Payment successful
                            </div>
                        </div>

                        <div className="px-6 py-7 text-center sm:px-8">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_10px_24px_-8px_rgba(16,185,129,0.9)]">
                                <Icon name="Check" size={27} strokeWidth={3} />
                            </div>
                            <h2 className="text-[26px] font-extrabold tracking-[-0.03em] text-slate-950">Order confirmed</h2>
                            <p className="mt-1.5 text-sm leading-5 text-slate-500">{successMsg}</p>

                            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-4 text-left">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="text-[10px] font-bold  tracking-[0.14em] text-slate-400">Plan added</p>
                                        <p className="mt-1 text-sm font-bold text-slate-900">{selectedPlan?.name || 'Interview pack'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-bold  tracking-[0.14em] text-slate-400">Credits</p>
                                        <p className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-emerald-600">
                                            <Icon name="Coins" size={14} />
                                            {selectedPlan?.coinsIncluded || 0}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex items-center gap-2 border-t border-slate-200 pt-3 text-xs text-slate-500">
                                    <Icon name="ShieldCheck" size={14} className="text-emerald-500" />
                                    Credits are now available in your account.
                                </div>
                            </div>

                            <div className="mt-6 space-y-2">
                                <Button className="h-11 w-full rounded-xl bg-emerald-500 text-sm font-bold shadow-sm shadow-emerald-200 transition-colors hover:bg-emerald-600" onClick={() => navigate('/dashboard')}>
                                    View Dashboard
                                    <Icon name="ArrowRight" size={16} className="ml-2" />
                                </Button>
                                <Button variant="ghost" className="h-9 w-full text-sm font-semibold text-slate-500 hover:text-emerald-600" onClick={() => setSuccess(false)}>
                                    Buy another pack
                                </Button>
                            </div>
                            <p className="mt-5 text-[11px] text-slate-400">Secure payment powered by Razorpay</p>
                        </div>
                    </motion.div>
                </main>
            </div>
        );
    }

    return (
        <div className="relative flex min-h-screen flex-col overflow-hidden bg-[linear-gradient(180deg,#ecfdf5_0%,#f7faf9_28%,#f7faf9_100%)] font-sans text-slate-900">

            <div className="relative z-10">
                <NavigationHeader />

                <main className="mx-auto w-full max-w-6xl flex-1 px-5 pb-12 pt-20 sm:px-8">
                    <div className="mb-8 flex items-end justify-between gap-4">
                        <div>
                            <button type="button" onClick={() => navigate('/dashboard')} className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-emerald-600">
                                <Icon name="ArrowLeft" size={14} />
                                Back to dashboard
                            </button>
                            {/* <p className="mb-2 text-[10px] font-black  tracking-[0.2em] text-emerald-600">prepxai credits</p> */}
                            <h1 className="text-[22px] font-extrabold tracking-[-0.025em] text-slate-950 dark:text-slate-100 sm:text-2xl">Choose a plan for your next interview</h1>
                            <p className="mt-1.5 max-w-xl text-xs leading-5 text-slate-500 dark:text-slate-400">Pick a pack and continue to secure payment.</p>
                        </div>
                        <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-500 shadow-sm sm:flex">
                            <Icon name="ShieldCheck" size={14} className="text-emerald-500" />
                            Secure checkout
                        </div>
                    </div>

                    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                        <section>
                            <div className="mb-3 flex items-center justify-between">
                                <h2 className="text-sm font-extrabold tracking-tight text-slate-900">Available plans</h2>
                                <span className="text-[11px] font-medium text-slate-400">Select one to continue</span>
                            </div>

                            {errors.main && (
                                <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-bold text-red-600">
                                    {errors.main}
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                    {loadingPlans
                                        ? [1, 2, 3].map((item) => (
                                            <div key={item} className="h-[228px] animate-pulse rounded-2xl border border-slate-200 bg-white" />
                                        ))
                                        : plans.map((pack) => (
                                            <PackCard
                                                key={pack.id}
                                                pack={pack}
                                                selected={selectedPackId === pack.id}
                                                onClick={() => {
                                                    setSelectedPackId(pack.id);
                                                    setCouponCode('');
                                                    setAppliedCoupon(null);
                                                    setCouponError(null);
                                                }}
                                            />
                                        ))}
                            </div>

                            {!loadingPlans && plans.length === 0 && (
                                <div className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm">
                                    <Icon name="ShoppingCart" size={30} className="mx-auto mb-3 text-slate-300" />
                                    <p className="text-sm font-bold text-slate-600">No plans are available right now.</p>
                                    <p className="mt-1 text-xs text-slate-400">Please try again in a moment.</p>
                                </div>
                            )}
                        </section>

                        <aside className="w-full lg:sticky lg:top-20 lg:max-w-[300px] lg:justify-self-end">
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] dark:border-[#292b35] dark:bg-[#111216]">
                                <div className="border-b border-slate-100 px-4 py-3 dark:border-[#292b35]">
                                    <p className="text-[10px] font-black  tracking-[0.18em] text-slate-400">Order summary</p>
                                </div>

                                {selectedPlan ? (
                                    <div>
                                        <div className="px-4 py-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <h4 className="text-sm font-extrabold tracking-tight text-slate-950 dark:text-slate-100">{selectedPlan.name}</h4>
                                                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-slate-500 dark:text-slate-400">{selectedPlan.meta.bestFor}</p>
                                                </div>
                                                <span className="text-base font-extrabold text-slate-950 dark:text-slate-100">₹{basePrice}</span>
                                            </div>
                                            <div className="mt-4 space-y-2.5 border-t border-slate-100 pt-3 dark:border-[#292b35]">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500">Interview credits</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-100">{selectedPlan.coinsIncluded || 'Included'}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-slate-500">Payment</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-100">One-time</span>
                                                </div>
                                            </div>
                                            <div className="mt-4 border-t border-slate-100 pt-3 dark:border-[#292b35]">
                                                <label htmlFor="checkout-coupon" className="mb-1.5 block text-[10px] font-bold tracking-[0.12em] text-slate-400">
                                                    Have a coupon?
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        id="checkout-coupon"
                                                        type="text"
                                                        value={couponCode}
                                                        onChange={(event) => {
                                                            setCouponCode(event.target.value.toUpperCase());
                                                            setCouponError(null);
                                                        }}
                                                        placeholder="Enter code"
                                                        className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 dark:border-[#292b35] dark:bg-[#17181e] dark:text-slate-100 dark:placeholder:text-slate-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={handleApplyCoupon}
                                                        disabled={!couponCode.trim() || validatingCoupon}
                                                        className="rounded-lg border border-slate-200 bg-white px-3 text-[10px] font-extrabold tracking-wide text-slate-600 transition hover:border-emerald-300 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#292b35] dark:bg-[#17181e] dark:text-slate-300"
                                                    >
                                                        {validatingCoupon ? '...' : 'Apply'}
                                                    </button>
                                                </div>
                                                {appliedCoupon && (
                                                    <p className="mt-1.5 text-[10px] font-bold text-emerald-600">
                                                        Coupon applied · saving ₹{discountAmount.toFixed(2)}
                                                    </p>
                                                )}
                                                {couponError && <p className="mt-1.5 text-[10px] font-semibold text-red-500">{couponError}</p>}
                                            </div>
                                        </div>

                                        <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-[#292b35] dark:bg-[#17181e]">
                                            <div className="flex items-end justify-between">
                                                <span className="text-xs font-semibold text-slate-500">Total</span>
                                                <span className="text-xl font-black tracking-tight text-slate-950 dark:text-slate-100">₹{finalPrice.toFixed(2)}</span>
                                            </div>
                                            <p className="mt-0.5 text-right text-[9px] font-medium text-slate-400">INR · taxes included where applicable</p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handlePayment}
                                            disabled={processing}
                                            className="mx-4 mb-2.5 flex h-10 w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-xl bg-emerald-500 text-xs font-extrabold text-white shadow-lg shadow-emerald-200/50 transition-all hover:bg-emerald-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {processing ? (
                                                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                            ) : (
                                                <>
                                                    <Icon name="Lock" size={15} strokeWidth={2.5} />
                                                    <span>Continue to payment</span>
                                                </>
                                            )}
                                        </button>

                                        <p className="px-4 pb-4 text-center text-[10px] leading-4 text-slate-400">
                                            Secure payment powered by Razorpay.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="px-5 py-16 text-center">
                                        <Icon name="ShoppingCart" size={30} className="mx-auto mb-3 text-slate-300" />
                                        <p className="text-sm font-bold text-slate-500">Select a plan to continue</p>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Checkout;
